import React from 'react'
import { useDroppable } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Button from 'components/Form/Button'
import {
  DeleteOutlineOutlined,
  DragIndicatorOutlined,
  EditOutlined,
  FolderOpenOutlined,
} from '@mui/icons-material'
import { Load } from 'ui/styled'
import { buildFolderDragId, UNFILED } from '../utils'
import {
  FolderActions,
  FolderCaption,
  FolderCount,
  FolderCreateRow,
  FolderMeta,
  FolderName,
  FolderRow,
  IconButton,
  InlineActions,
  InlineInput,
} from './styled'

function StaticFolderTarget({ active, count, name, caption, onClick, dimmed }) {
  const { isOver, setNodeRef } = useDroppable({
    id: buildFolderDragId(UNFILED),
    data: {
      type: 'folder',
      folderId: null,
    },
  })

  return (
    <FolderRow
      ref={setNodeRef}
      active={active || isOver}
      onClick={onClick}
      dimmed={dimmed}
      role="button"
      tabIndex={0}
    >
      <FolderOpenOutlined fontSize="small" />
      <FolderMeta>
        <FolderName>{name}</FolderName>
        <FolderCaption>{caption}</FolderCaption>
      </FolderMeta>
      <FolderCount>{count}</FolderCount>
      <div />
    </FolderRow>
  )
}

function SortableFolderTarget({
  folder,
  active,
  onClick,
  onStartEdit,
  onDelete,
  isBusy,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: buildFolderDragId(folder.id),
    data: {
      type: 'folder',
      folderId: folder.id,
    },
    disabled: isBusy,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <FolderRow
      ref={setNodeRef}
      style={style}
      active={active || isOver}
      dragging={isDragging}
      onClick={onClick}
      dimmed={isBusy}
      role="button"
      tabIndex={0}
      {...attributes}
      {...listeners}
    >
      <DragIndicatorOutlined fontSize="small" />
      <FolderMeta>
        <FolderName>{folder.name}</FolderName>
        <FolderCaption>{folder.count} item{folder.count === 1 ? '' : 's'}</FolderCaption>
      </FolderMeta>
      <FolderCount>{folder.count}</FolderCount>
      <FolderActions onClick={(event) => event.stopPropagation()}>
        <IconButton type="button" onClick={onStartEdit} disabled={isBusy}>
          <EditOutlined fontSize="small" />
        </IconButton>
        <IconButton type="button" onClick={onDelete} disabled={isBusy}>
          {isBusy ? <Load /> : <DeleteOutlineOutlined fontSize="small" />}
        </IconButton>
      </FolderActions>
    </FolderRow>
  )
}

export default function FavoritesFolderTarget(props) {
  if (!props.folder) {
    return <StaticFolderTarget {...props} />
  }

  if (props.editing) {
    return (
      <FolderCreateRow>
        <InlineInput
          autoFocus
          value={props.editingName}
          onChange={(event) => props.onEditingNameChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') props.onEditingSubmit()
            if (event.key === 'Escape') props.onEditingCancel()
          }}
          placeholder={props.placeholder}
        />
        <InlineActions>
          <Button small color="primary" width="fit-content" onClick={props.onEditingSubmit}>
            {props.doneLabel}
          </Button>
          <Button small outline color="primary" width="fit-content" onClick={props.onEditingCancel}>
            {props.cancelLabel}
          </Button>
        </InlineActions>
      </FolderCreateRow>
    )
  }

  return <SortableFolderTarget {...props} />
}
